import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/system';
import { motion } from 'framer-motion';
import { getBusinessMetrics, getUserMetrics, getValetMetrics, getValets, getUsers, getServices } from './services/adminService';
import BusinessTab from './components/admin/BusinessTab';
import UsersTab from './components/admin/UsersTab';
import ValetsTab from './components/admin/ValetsTab';
import CustomersTab from './components/admin/CustomersTab';
import SubscribersTab from './components/admin/SubscribersTab';
import ServicesTab from './components/admin/ServicesTab';
import PayoutsTab from './components/admin/PayoutsTab';
import PhotosTab from './components/admin/PhotosTab';
import PricingTab from './components/admin/PricingTab';
import NotificationsTab from './components/admin/NotificationsTab';
import PostersTab from './components/admin/PostersTab';
import DecksTab from './components/admin/DecksTab';
import AdminKeyGate from './components/admin/AdminKeyGate';
import { getAdminKey, clearAdminKey, AdminKeyRejected } from './services/adminKey';
import { VALET, FONTS, TYPE } from './theme';

const PERIODS = ['Today', '7 days', '30 days', '90 days'];
const TAB_NAMES = ['Business', 'Users', 'Valets', 'Customers', 'Subscribers', 'Services', 'Payouts', 'Photos', 'Pricing', 'Notifications', 'Posters', 'Decks'];

const DashboardRoot = styled(Box)({
  fontFamily: FONTS.sans,
  color: VALET.textPrimary,
  backgroundColor: VALET.bgMuted,
  minHeight: '100vh',
});

const Nav = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  padding: '16px 28px',
  borderBottom: `1px solid ${VALET.bronzeHairline}`,
  backgroundColor: VALET.bg,
  position: 'sticky',
  top: 0,
  zIndex: 10,
  // On a phone the brand row and the period pills cannot share one line, so the
  // nav wraps and gives up its sticky slot — vertical space is scarcer than the
  // convenience of a pinned logo, and the tab bar below takes over the top edge.
  '@media (max-width: 768px)': {
    position: 'static',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    padding: '12px 16px',
  },
});

const LogoBox = styled(Box)({
  width: 34,
  height: 34,
  borderRadius: 8,
  background: VALET.green,
  color: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: FONTS.display,
  fontSize: 17,
  fontWeight: 600,
  letterSpacing: '-0.02em',
});

const BrandTitle = styled(Typography)({
  fontFamily: FONTS.display,
  fontWeight: 500,
  fontSize: '17px',
  letterSpacing: '-0.025em',
  color: VALET.ink,
});

const BrandSubtitle = styled(Typography)({
  fontFamily: FONTS.mono,
  fontSize: '10px',
  fontWeight: 500,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: VALET.bronze,
  paddingLeft: '12px',
  marginLeft: '8px',
  borderLeft: `1px solid ${VALET.bronzeHairline}`,
  '@media (max-width: 768px)': {
    display: 'none',
  },
});

const TabBar = styled(Box)({
  borderBottom: `1px solid ${VALET.border}`,
  backgroundColor: VALET.bg,
  position: 'sticky',
  // 67px is the desktop nav's height, which the tab bar has to clear. On mobile
  // the nav is no longer sticky, so there is nothing to clear.
  top: 67,
  zIndex: 9,
  '@media (max-width: 768px)': {
    top: 0,
  },
});

const StyledTab = styled(Tab)({
  padding: '14px 22px',
  // Tighter and non-shrinking so the scrollable strip pages cleanly by thumb.
  '@media (max-width: 768px)': {
    padding: '12px 14px',
    minWidth: 'auto',
    flexShrink: 0,
  },
  fontSize: '13px',
  letterSpacing: '-0.01em',
  textTransform: 'none',
  fontFamily: FONTS.sans,
  fontWeight: 500,
  color: VALET.textMuted,
  minHeight: 'auto',
  transition: 'color 180ms ease',
  '&:hover': {
    color: VALET.textPrimary,
  },
  '&.Mui-selected': {
    color: VALET.green,
    fontWeight: 600,
  },
});

const Body = styled(Box)({
  padding: '32px 28px',
  maxWidth: 1440,
  margin: '0 auto',
  minHeight: 'calc(100vh - 120px)',
  '@media (max-width: 768px)': {
    padding: '20px 14px 40px',
    minHeight: 'auto',
  },
});

const PeriodToggle = styled(ToggleButton)({
  padding: '6px 14px',
  borderRadius: '999px !important',
  fontSize: '12px',
  fontFamily: FONTS.sans,
  fontWeight: 500,
  letterSpacing: '-0.005em',
  textTransform: 'none',
  border: `1px solid ${VALET.border} !important`,
  color: VALET.textSecondary,
  backgroundColor: VALET.bg,
  transition: 'all 160ms ease',
  '&:hover': {
    backgroundColor: VALET.greenLight,
    color: VALET.green,
    borderColor: `${VALET.greenHairline} !important`,
  },
  '&.Mui-selected': {
    backgroundColor: VALET.green,
    color: '#ffffff',
    border: `1px solid ${VALET.green} !important`,
    '&:hover': {
      backgroundColor: VALET.greenDark,
    },
  },
});

const DateLabel = styled(Typography)({
  ...TYPE.eyebrow,
  color: VALET.textMuted,
});

const ErrorMessage = styled(Typography)({
  color: VALET.danger,
  backgroundColor: VALET.dangerBg,
  fontSize: '13px',
  padding: '14px 18px',
  borderRadius: '8px',
  border: `1px solid ${VALET.danger}33`,
  fontWeight: 500,
  fontFamily: FONTS.sans,
});

export default function AdminDashboard() {
  // Every /api/admin call needs the operator's key. Start locked unless this
  // browser tab already has one; a 401 from any call drops us back here.
  const [unlocked, setUnlocked] = useState(() => !!getAdminKey());
  const [tab, setTab] = useState(0);
  const [period, setPeriod] = useState('7 days');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bizData, setBizData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [valetData, setValetData] = useState(null);
  const [rosterData, setRosterData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [rosterError, setRosterError] = useState(null);
  const [customerError, setCustomerError] = useState(null);
  const [serviceData, setServiceData] = useState(null);
  const [serviceError, setServiceError] = useState(null);

  // A rejected or revoked key sends the whole console back to the unlock
  // screen. Cached tab data is dropped so the next unlock refetches rather than
  // showing whoever was signed in before.
  const relock = useCallback(() => {
    clearAdminKey();
    setUnlocked(false);
    setBizData(null);
    setUserData(null);
    setValetData(null);
    setRosterData(null);
    setRosterError(null);
    setCustomerData(null);
    setCustomerError(null);
    setServiceData(null);
    setServiceError(null);
    setError(null);
  }, []);

  const fetchData = useCallback(async (selectedPeriod) => {
    setLoading(true);
    setError(null);
    try {
      const [bizRes, userRes, valetRes] = await Promise.all([
        getBusinessMetrics(selectedPeriod),
        getUserMetrics(selectedPeriod),
        getValetMetrics(selectedPeriod),
      ]);
      setBizData(bizRes.success ? bizRes.data : bizRes);
      setUserData(userRes.success ? userRes.data : userRes);
      setValetData(valetRes.success ? valetRes.data : valetRes);
    } catch (err) {
      if (err instanceof AdminKeyRejected) {
        relock();
        return;
      }
      console.error('Dashboard fetch error:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [relock]);

  useEffect(() => {
    if (!unlocked) return;
    fetchData(period);
  }, [unlocked, period, fetchData]);

  useEffect(() => {
    if (!unlocked) return;
    // A 401 on any tab means the key died, not that this one list is broken.
    const onTabError = (err, setTabError, fallback) => {
      if (err instanceof AdminKeyRejected) relock();
      else setTabError(err.message || fallback);
    };
    if (tab === 2 && rosterData === null && rosterError === null) {
      getValets()
        .then((res) => setRosterData(res && res.success ? res.data : res))
        .catch((err) => onTabError(err, setRosterError, 'Failed to load valet roster'));
    }
    if (tab === 3 && customerData === null && customerError === null) {
      getUsers()
        // The whole payload, not just the rows: it carries New York's date,
        // which is what the cleaning-day column reads "today" off.
        .then((res) => setCustomerData(res))
        .catch((err) => onTabError(err, setCustomerError, 'Failed to load customers'));
    }
    if (tab === 5 && serviceData === null && serviceError === null) {
      getServices()
        .then((res) => setServiceData(res && res.success ? res.data : res))
        .catch((err) => onTabError(err, setServiceError, 'Failed to load service requests'));
    }
  }, [unlocked, relock, tab, rosterData, customerData, rosterError, customerError, serviceData, serviceError]);

  const handlePeriodChange = (_, newPeriod) => {
    if (newPeriod) setPeriod(newPeriod);
  };

  const dateStr = new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  if (!unlocked) {
    return <AdminKeyGate onUnlocked={() => setUnlocked(true)} />;
  }

  return (
    <DashboardRoot>
      <Nav>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <LogoBox>V</LogoBox>
          <BrandTitle>Valet Network</BrandTitle>
          <BrandSubtitle>Operator console</BrandSubtitle>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            // Below 768 this drops onto its own full-width line under the brand
            // and scrolls sideways rather than squeezing four pills into 340px.
            '@media (max-width: 768px)': {
              width: '100%',
              gap: '10px',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            },
          }}
        >
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={handlePeriodChange}
            size="small"
            sx={{ gap: '6px', flexShrink: 0 }}
          >
            {PERIODS.map((p) => (
              <PeriodToggle key={p} value={p}>{p}</PeriodToggle>
            ))}
          </ToggleButtonGroup>
          {/* Today's date is decoration; the pills are the control. */}
          <DateLabel sx={{ '@media (max-width: 768px)': { display: 'none' } }}>
            {dateStr}
          </DateLabel>
        </Box>
      </Nav>

      <TabBar>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            minHeight: 'auto',
            px: '28px',
            maxWidth: 1440,
            margin: '0 auto',
            '& .MuiTabs-indicator': {
              backgroundColor: VALET.green,
              height: 2,
            },
            // Eleven tabs will never fit a phone. Scrollable keeps them on one
            // swipeable strip; the scrollbar itself is hidden because the
            // half-visible next tab is the affordance.
            '& .MuiTabs-scroller': {
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            },
            '@media (max-width: 768px)': {
              px: '8px',
            },
          }}
        >
          {TAB_NAMES.map((name) => (
            <StyledTab key={name} label={name} disableRipple />
          ))}
        </Tabs>
      </TabBar>

      <Body>
        {/* No AnimatePresence here. Under React 18 StrictMode, framer-motion v11
            leaves the exiting panel registered forever: with mode="wait" the next
            panel never mounted (every tab showed Business), and without it the old
            panel never unmounted (panels stacked). Keying the motion.div on `tab`
            makes React swap panels itself, and the enter animation still plays. */}
        <motion.div
            key={tab}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.22, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {tab <= 2 && loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                <CircularProgress sx={{ color: VALET.green }} />
              </Box>
            ) : tab <= 2 && error ? (
              <ErrorMessage>{error}</ErrorMessage>
            ) : (
              <>
                {tab === 0 && <BusinessTab data={bizData} />}
                {tab === 1 && <UsersTab data={userData} />}
                {tab === 2 && <ValetsTab data={valetData} rosterData={rosterData} rosterError={rosterError} />}
                {tab === 3 && (
                  customerError ? <ErrorMessage>{customerError}</ErrorMessage>
                  : customerData === null ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                      <CircularProgress sx={{ color: VALET.green }} />
                    </Box>
                  ) : <CustomersTab data={customerData} />
                )}
                {/* Subscribers fetches its own data — one call, no shared state to
                    thread through the shell. */}
                {tab === 4 && <SubscribersTab />}
                {tab === 5 && (
                  serviceError
                    ? <div style={{ padding: '24px', color: '#a03028' }}>{serviceError}</div>
                    : <ServicesTab data={serviceData} />
                )}
                {tab === 6 && <PayoutsTab />}
                {tab === 7 && <PhotosTab />}
                {tab === 8 && <PricingTab />}
                {tab === 9 && <NotificationsTab />}
                {tab === 10 && <PostersTab />}
                {tab === 11 && <DecksTab />}
              </>
            )}
        </motion.div>
      </Body>
    </DashboardRoot>
  );
}
